/** One `and` group of scalar conditions (depth 1), or null when nothing is active. */
export function quickFiltersToAst(filters, fields, toDateBounds) {
    const types = new Map(fields.map((f) => [f.path, f.type]));
    const conditions = [];
    for (const [path, value] of Object.entries(filters ?? {})) {
        const type = types.get(path);
        if (type)
            conditions.push(...toConditions(path, type, value, toDateBounds));
    }
    return conditions.length > 0 ? { operator: "and", conditions } : null;
}
/**
 * ANDs a free-text search into `refinement`: a case-insensitive `contains` on any of `fields`. A single field adds a
 * plain condition rather than a group, so it costs no OPQRY nesting level.
 */
export function withSearchRefinement(refinement, search, fields) {
    const term = search?.trim();
    if (!term || fields.length === 0)
        return refinement;
    const matches = fields.map((field) => ({ field, op: "contains", value: term }));
    const searchNode = matches.length === 1 ? matches[0] : { operator: "or", conditions: matches };
    if (!refinement)
        return { operator: "and", conditions: [searchNode] };
    const base = refinement.operator === "and" ? refinement.conditions : [refinement];
    return { operator: "and", conditions: [...base, searchNode] };
}
function toConditions(field, type, value, toDateBounds) {
    switch (value.type) {
        case "equals": {
            const text = String(value.value).trim();
            if (!text)
                return [];
            return [{ field, op: type === "String" ? "contains" : "eq", value: text }];
        }
        case "in":
            if (value.values.length === 0)
                return [];
            // A Bool field is edited as a single-choice "true"/"false" list (`toEditor`); any other shape comes from a
            // tampered tab and is not applied.
            if (type === "Bool") {
                const [choice] = value.values;
                return value.values.length === 1 && (choice === "true" || choice === "false")
                    ? [{ field, op: "eq", value: choice === "true" }]
                    : [];
            }
            return [{ field, op: "in", values: [...value.values] }];
        case "range": {
            const out = [];
            if (value.min != null)
                out.push({ field, op: "gte", value: value.min });
            if (value.max != null)
                out.push({ field, op: "lte", value: value.max });
            return out;
        }
        case "dateRange": {
            const { from, before } = toDateBounds(value.from, value.to, field);
            const out = [];
            if (from)
                out.push({ field, op: "gte", value: from });
            if (before)
                out.push({ field, op: "lt", value: before });
            return out;
        }
        case "boolean":
            return [{ field, op: "eq", value: value.value }];
    }
}
